import { createSlice } from "@reduxjs/toolkit";
import { getFinalWorklistData } from "../../../api/finalWorklistData/fetchFinalWorklistData";
import {
  FinalWorklistDataResponse,
  FinalWorklistPayload,
  Pagination,
} from "../../../data-model/finalWorklistModel";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

export interface FinalWorklistOrders {
  data: FinalWorklistDataResponse[] | null;
  loading: boolean;
  error: null | any;
  pageData: null | Pagination;
  showInLineLoader?: boolean;
}

const initialState: FinalWorklistOrders = {
  data: null,
  loading: false,
  error: null,
  pageData: null,
  showInLineLoader: false,
};

const finalWorklistSlice = createSlice({
  name: "finalWorklistOrders",
  initialState,
  reducers: {
    fetchingOrders: (state) => {
      state.loading = true;
      state.data = null;
      state.pageData = null;
    },
    setOrdersData: (state, action) => {
      const { ordersData, pageData } = action.payload;
      state.data = ordersData;
      state.pageData = pageData;
      state.loading = false;
    },
    errorFetchingOrders: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetFinalWorklistOrders: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.pageData = null;
    },
    appendOrdersData: (state, action) => {
      const { ordersData, pageData } = action.payload;
      state.data = state.data ? [...state.data, ...ordersData] : ordersData;
      state.pageData = pageData;
      state.loading = false;
    },
    showInLineLoader: (state) => {
      state.showInLineLoader = true;
    },
    hideInLineLoader: (state) => {
      state.showInLineLoader = false;
    },
  },
});

export const { resetFinalWorklistOrders } = finalWorklistSlice.actions;

export const fetchFinalWorklistOrdersFromApi =
  (limit?: number, filter?: any) => async (dispatch, getState) => {
    const { finalWorklistOrdersData } = getState();
    const orgId = getOrgIdForFetchExternalWorklist();

    if (finalWorklistOrdersData.loading) {
      return;
    }

    dispatch(finalWorklistSlice.actions.fetchingOrders());

    try {
      const payload: FinalWorklistPayload = {
        org_id: orgId,
        sort_by: "date_of_work",
        sort_order: "DESC",
        page: 1,
        page_size: limit ?? 100,
        ...filter,
      };

      const response = await getFinalWorklistData(payload);
      const pageData = response.pagination;
      const ordersData = response.data;

      dispatch(
        finalWorklistSlice.actions.setOrdersData({ ordersData, pageData }),
      );
    } catch (error) {
      dispatch(
        finalWorklistSlice.actions.errorFetchingOrders(
          "Error fetching final worklist orders",
        ),
      );
    }
  };

export const appendFinalWorklistOrdersFromApi =
  (limit?: number, filter?: any) => async (dispatch, getState) => {
    const { finalWorklistOrdersData } = getState();
    const orgId = getOrgIdForFetchExternalWorklist();

    const { pageData, loading } = finalWorklistOrdersData;
    if (loading || !pageData || !pageData.has_next) {
      return;
    }

    try {
      dispatch(finalWorklistSlice.actions.showInLineLoader());
      const nextPage = pageData.current_page + 1;

      const payload: FinalWorklistPayload = {
        org_id: orgId,
        sort_by: "date_of_work",
        sort_order: "DESC",
        page: nextPage,
        page_size: limit ?? 100,
        ...filter,
      };

      const response = await getFinalWorklistData(payload);
      const newPageData = response.pagination;
      const newOrdersData = response.data;

      dispatch(
        finalWorklistSlice.actions.appendOrdersData({
          ordersData: newOrdersData,
          pageData: newPageData,
        }),
      );
    } catch (error) {
      dispatch(
        finalWorklistSlice.actions.errorFetchingOrders(
          "Error fetching more final worklist orders",
        ),
      );
    } finally {
      dispatch(finalWorklistSlice.actions.hideInLineLoader());
    }
  };

export const searchFinalWorklistOrdersFromApi =
  (keyword: string, limit?: number) => async (dispatch, getState) => {
    const orgId = getOrgIdForFetchExternalWorklist();

    dispatch(finalWorklistSlice.actions.fetchingOrders());

    try {
      const isAllAlphabets = /^[a-zA-Z\s]+$/.test(keyword.trim());
      const containsDigits = /\d/.test(keyword);

      let searchField: Record<string, string> = {};

      if (isAllAlphabets && !containsDigits) {
        searchField = { patient_name: keyword.trim() };
      } else if (containsDigits) {
        searchField = { patient_id: keyword.trim() };
      }

      const payload: FinalWorklistPayload = {
        org_id: orgId,
        sort_by: "date_of_work",
        sort_order: "DESC",
        page: 1,
        page_size: limit ?? 100,
        ...searchField,
      };

      const response = await getFinalWorklistData(payload);
      const pageData = response.pagination;
      const ordersData = response.data;

      dispatch(
        finalWorklistSlice.actions.setOrdersData({
          ordersData,
          pageData,
        }),
      );
    } catch (error) {
      dispatch(
        finalWorklistSlice.actions.errorFetchingOrders(
          "Error searching final worklist orders",
        ),
      );
    }
  };

export const appendFinalWorklistSearchedOrdersFromApi =
  (keyword: string, limit?: number) => async (dispatch, getState) => {
    const { finalWorklistOrdersData } = getState();
    const orgId = getOrgIdForFetchExternalWorklist();

    const { pageData, loading } = finalWorklistOrdersData;
    if (loading || !pageData || !pageData.has_next) {
      return;
    }

    try {
      dispatch(finalWorklistSlice.actions.showInLineLoader());
      const nextPage = pageData.current_page + 1;

      const isAllAlphabets = /^[a-zA-Z\s]+$/.test(keyword.trim());
      const containsDigits = /\d/.test(keyword);

      let searchField: Record<string, string> = {};

      if (isAllAlphabets && !containsDigits) {
        searchField = { patient_name: keyword.trim() };
      } else if (containsDigits) {
        searchField = { patient_id: keyword.trim() };
      }

      const payload: FinalWorklistPayload = {
        org_id: orgId,
        sort_by: "date_of_work",
        sort_order: "DESC",
        page: nextPage,
        page_size: limit ?? 100,
        ...searchField,
      };

      const response = await getFinalWorklistData(payload);
      const newPageData = response.pagination;
      const newOrdersData = response.data;

      dispatch(
        finalWorklistSlice.actions.appendOrdersData({
          ordersData: newOrdersData,
          pageData: newPageData,
        }),
      );
    } catch (error) {
      dispatch(
        finalWorklistSlice.actions.errorFetchingOrders(
          "Error fetching more searched final worklist orders",
        ),
      );
    } finally {
      dispatch(finalWorklistSlice.actions.hideInLineLoader());
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { finalWorklistOrdersData } = state;
  const { data, pageData } = finalWorklistOrdersData;

  return pageData && pageData.has_next && data && endIndex === data.length - 1;
};

export default finalWorklistSlice.reducer;
